// AP Aging projection — mirrors Finance.API ApAgingReportDto.
// Open payables bucketed into Wednesday-opening payment weeks by due date and grouped by provider.
import { ResponseTranslation } from '../Translations';
import { ApDocumentStatus, ApDocumentType, Currency } from '../ap-document/ap-document';

/** What a column represents. Overdue and Beyond are open-ended; only Week carries dates. */
export enum ApAgingBucketKind {
  Overdue = 1,
  Week = 2,
  Beyond = 3,
}

/** Reserved column index for the overdue bucket, which sits before week 0. */
export const OVERDUE_BUCKET_INDEX = -1;

export interface ApAgingCell {
  index: number;
  kind: ApAgingBucketKind;
  /** Null for Overdue and Beyond. */
  weekStarting: string | null;
  weekEnding: string | null;
  /** Signed — a week whose only open document is a standalone credit note is genuinely negative. */
  amount: number;
  documentCount: number;
  blockedDocumentCount: number;
  /** Included in `amount`, not deducted from it. */
  blockedAmount: number;
}

export interface ApAgingDocument {
  apDocumentReference: string;
  number: string;
  documentType: ApDocumentType;
  status: ApDocumentStatus;
  fiscalNumber: string | null;

  providerReference: string | null;
  providerCode: string;
  providerName: string;

  documentDate: string;
  dueDate: string;
  creditDays: number;
  /** Null when not yet due. */
  daysPastDue: number | null;

  currency: Currency;
  /** The rate the document was booked at, not today's. */
  exchangeRate: number;
  /** Signed, document currency. */
  netPayable: number;
  /** Signed, reporting currency — each component converted at its own booked rate. */
  amount: number;

  bucketIndex: number;
  isOverdue: boolean;
  isBeyondHorizon: boolean;

  balanceDue: number;
  creditNoteAmount: number;
  debitNoteAmount: number;
  withheldAmount: number;
  hasAdjustments: boolean;
  /** A note whose base is already settled; a standalone credit note carries a negative amount. */
  isStandaloneNote: boolean;

  withholdingSatisfied: boolean;
  hasPostedWithholding: boolean;
  /** Shown but not payable — still counted in the requirement. */
  isBlocked: boolean;
  blockedReason: string | null;

  /** A provider's documents can sit on different control accounts, so this rides on the row. */
  apAccountReference: string;
  apAccountNumber: string;
  apAccountName: ResponseTranslation | null;
}

export interface ApAgingProviderGroup {
  /** Empty for a one-time vendor, which has no provider master record. */
  providerReference: string;
  providerCode: string;
  providerName: string;
  /** Always weeks + 2 entries, in the same index order as the report's `totals`. */
  cells: ApAgingCell[];
  /** Empty on the summary — fetched per provider when its row is opened. */
  documents: ApAgingDocument[];
  subtotalAmount: number;
  documentCount: number;
  blockedDocumentCount: number;
  blockedAmount: number;
}

export interface ApAgingReport {
  generatedAt: string;
  /** What was asked for, before snapping to the week's Wednesday. */
  requestedAnchorDate: string;
  /** The Wednesday the first week opens on. */
  anchorDate: string;
  weeks: number;
  reportingCurrency: Currency;
  financialCompanyReference: string;
  financialCompanyName: string;
  /** The grand-total row and the column definitions in one: weeks + 2 cells, index order -1..weeks. */
  totals: ApAgingCell[];
  providerGroups: ApAgingProviderGroup[];
  /** Ties to total open AP — the report's reconciliation invariant. */
  grandTotal: number;
  documentCount: number;
  blockedDocumentCount: number;
  blockedAmount: number;
}

export interface ApAgingCommand {
  /** Any date in the week to start from; the API snaps it to that week's Wednesday. */
  anchorDate?: string | null;
  weeks: number;
  /** Documents call only. Empty string means the one-time vendors. Ignored by the summary. */
  providerReference?: string;
}

/** Which of the two workbooks to render. Mirrors `ApAgingExportKind` on the API. */
export enum ApAgingExportKind {
  ByProvider = 1,
  ByDocument = 2,
}

/**
 * An export request: the same grid the screen was built with, plus which view to render.
 *
 * The anchor and week count are deliberately the report's own, so the workbook is the file version of what
 * the user is looking at rather than a second set of parameters that could drift.
 */
export interface ApAgingExportCommand extends ApAgingCommand {
  kind: ApAgingExportKind;
  /** Restricts the workbook to these providers; empty means all of them. Mirrors the screen's filter. */
  providerReferences?: string[];
}
